import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type HandoffAudit = {
  tenant_id: string;
  conversation_id: string;
  handoff_id: string;
  closed_by: string | null;
  closed_at: string;
  close_reason: string | null;
  previous_status: string;
  new_status: string;
};

type CaseAuditEvent = {
  audit_id: string;
  actor_type: string;
  actor_id: string | null;
  event: string;
  previous_status: string;
  new_status: string;
  reason: string | null;
  created_at: string;
};

type OperationalAuditEvent = {
  audit_id: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  previous_state: Record<string, unknown> | null;
  new_state: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
};

export type CaseDetail = {
  case_id: string;
  tenant_id: string;
  conversation_id: string;
  customer_phone: string;
  status: string;
  priority: string;
  updated_at: string;
  needs_human_attention: boolean;
  outcome?: string | null;
  assigned_to?: string | null;
  active_handoff_id?: string | null;
  handoff_started_at?: string | null;
  handoff_closed_at?: string | null;
  handoff_closed_by?: string | null;
  handoff_close_reason?: string | null;
  human_service_stage?: string | null;
  pending_intent?: string | null;
  handoff_audit?: HandoffAudit[];
  case_audit?: CaseAuditEvent[];
};

export type MessageLog = {
  log_id: string;
  direction?: string;
  message?: { type: string; text?: string | null };
  message_text?: string | null;
  reply_text?: string | null;
  created_at?: string;
  timestamp?: string;
};

type CaseSummary = {
  summary_id: string;
  generated_at: string;
  generated_by: string;
  intent: string;
  status: string;
  priority: string;
  collected_data: Array<{ label: string; value: string; caution?: string }>;
  pending_items: string[];
  latest_interactions: Array<{
    direction: string;
    at: string | null;
    text: string;
    sensitive: boolean;
  }>;
  source_message_count: number;
  source_last_message_at: string | null;
  case_updated_at: string;
  config_version: string | null;
  privacy: {
    cautions: string[];
    redacted: boolean;
  };
};

type ReplySuggestion = {
  suggestion_id: string;
  text: string;
  editable: boolean;
  send: false;
  generated_by: string;
  template_key: string;
  template_id: string;
  template_version: string;
  template_scope: string;
  variables: Record<string, string>;
  reason: string;
  priority: string;
  policy_notes: string[];
  config_version: string | null;
  vertical_pack_id: string | null;
  vertical_pack_version: string | null;
};

type ReplySuggestionResult = {
  suggestion: ReplySuggestion;
};

type ReplyResult = {
  case: CaseDetail;
  message: MessageLog;
  delivery: {
    outbox_status: string;
    outgoing_wamid?: string | null;
    error?: string | null;
  };
  suggestion_audit?: Record<string, unknown> | null;
};

export const OUTCOME_OPTIONS = [
  { value: 'SCHEDULED', label: 'Agendado' },
  { value: 'QUOTE_SENT', label: 'Orcamento enviado' },
  { value: 'SALE_DONE', label: 'Venda realizada' },
  { value: 'QUESTION_ANSWERED', label: 'Duvida respondida' },
  { value: 'CUSTOMER_NO_REPLY', label: 'Cliente nao respondeu' },
  { value: 'CUSTOMER_DECLINED', label: 'Cliente recusou' },
  { value: 'SPAM_OR_TEST', label: 'Spam ou teste' },
  { value: 'CANCELLED', label: 'Cancelado' },
  { value: 'OTHER', label: 'Outro' },
];

const HUMAN_STATUSES = ['READY_FOR_STAFF', 'IN_STAFF_HANDS', 'NEEDS_REVIEW'];

export function isCaseClosed(c: CaseDetail | null) {
  if (!c) return false;
  return (
    ['CLOSED', 'DONE', 'CANCELLED'].includes(c.status) ||
    (c.needs_human_attention === false && c.status === 'CLOSED')
  );
}

export function canAssumeCase(c: CaseDetail | null) {
  if (!c || isCaseClosed(c)) return false;
  return ['READY_FOR_STAFF', 'NEEDS_REVIEW'].includes(c.status);
}

export function canSendHumanReply(c: CaseDetail | null) {
  if (!c || isCaseClosed(c)) return false;
  return c.needs_human_attention && HUMAN_STATUSES.includes(c.status);
}

export function canCloseCase(c: CaseDetail | null) {
  return canSendHumanReply(c);
}

export function canReturnToBot(c: CaseDetail | null) {
  if (!c || isCaseClosed(c)) return false;
  return Boolean(
    c.needs_human_attention && c.active_handoff_id && HUMAN_STATUSES.includes(c.status),
  );
}

export function botStatusLabel(c: CaseDetail | null) {
  if (!c) return 'Carregando';
  if (isCaseClosed(c)) return 'Bot inativo: case encerrado';
  return c.needs_human_attention ? 'Bot pausado: atendimento humano ativo' : 'Bot ativo';
}

export function ownerLabel(c: CaseDetail | null) {
  if (!c) return '-';
  return c.assigned_to ?? 'Sem responsavel';
}

export function nextStepsForCase(c: CaseDetail | null) {
  if (!c) return ['Carregar case'];
  if (isCaseClosed(c)) return ['Revisar auditoria e outcome'];
  if (c.status === 'READY_FOR_STAFF' || c.status === 'NEEDS_REVIEW') {
    return ['Assumir atendimento', 'Responder cliente', 'Encerrar ou devolver ao bot'];
  }
  if (c.status === 'IN_STAFF_HANDS') {
    return [
      'Responder cliente',
      'Encerrar com outcome',
      'Devolver ao bot se a automacao puder seguir',
    ];
  }
  if (c.status === 'WAITING_CUSTOMER_INFO') {
    return ['Bot aguardando informacao do cliente'];
  }
  return ['Acompanhar nova mensagem'];
}

export function buildReturnToBotHistoryEvent(c: CaseDetail): MessageLog {
  const audit = c.handoff_audit?.[c.handoff_audit.length - 1];
  return {
    log_id: `handoff-return-${audit?.handoff_id ?? c.case_id}-${audit?.closed_at ?? c.updated_at}`,
    direction: 'SYSTEM',
    created_at: audit?.closed_at ?? c.updated_at,
    message_text: `Atendimento humano encerrado${audit?.closed_by ? ` por ${audit.closed_by}` : ''}. Bot ativo.`,
  };
}

export function friendlyConsoleError(err: unknown) {
  const message = String(err instanceof Error ? err.message : err);
  if (message.includes('handoff_already_closed'))
    return 'Esse atendimento ja foi devolvido ao bot.';
  if (message.includes('missing_close_reason')) return 'Informe o motivo antes de encerrar.';
  if (message.includes('missing_or_invalid_outcome')) return 'Selecione um outcome valido.';
  if (message.includes('case_summary_disabled'))
    return 'Resumo automatico desativado para este tenant.';
  if (message.includes('reply_suggestions_disabled'))
    return 'Sugestoes desativadas para este tenant.';
  if (message.includes('reply_suggestion_unavailable_for_case'))
    return 'Sugestao indisponivel para o status atual.';
  if (message.includes('reply_suggestion_template_unavailable'))
    return 'Template de sugestao indisponivel.';
  if (message.includes('invalid_transition')) return 'Acao indisponivel para o status atual.';
  return message;
}

export function prependReturnToBotHistoryEvent(c: CaseDetail, current: MessageLog[]) {
  const event = buildReturnToBotHistoryEvent(c);
  if (current.some((message) => message.log_id === event.log_id)) return current;
  return [event, ...current];
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : '-';
}

function messageText(message: MessageLog) {
  return message.message_text ?? message.reply_text ?? message.message?.text ?? 'Sem texto';
}

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);
  const [messages, setMessages] = useState<MessageLog[]>([]);
  const [auditEvents, setAuditEvents] = useState<OperationalAuditEvent[]>([]);
  const [caseSummary, setCaseSummary] = useState<CaseSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySuggestion, setReplySuggestion] = useState<ReplySuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [closeOutcome, setCloseOutcome] = useState('QUESTION_ANSWERED');
  const [closeReason, setCloseReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchCaseSummary = useCallback(async () => {
    if (!tenant_id || !caseId) return null;
    try {
      const out = await request<{ summary: CaseSummary }>(
        `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}/summary`,
      );
      return out.summary;
    } catch (err) {
      if (String(err instanceof Error ? err.message : err).includes('case_summary_disabled')) {
        return null;
      }
      throw err;
    }
  }, [caseId, request, tenant_id]);

  const refreshCaseSummary = useCallback(async () => {
    setSummaryError(null);
    try {
      setCaseSummary(await fetchCaseSummary());
    } catch (err) {
      setSummaryError(friendlyConsoleError(err));
    }
  }, [fetchCaseSummary]);

  const load = useCallback(async () => {
    if (!tenant_id || !caseId) return;
    setLoading(true);
    setError(null);
    setSummaryError(null);
    try {
      const [caseOut, messageOut, auditOut, summaryOut] = await Promise.all([
        request<{ case: CaseDetail }>(
          `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}`,
        ),
        request<{ items: MessageLog[] }>(
          `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}/messages`,
        ),
        request<{ items: OperationalAuditEvent[] }>(
          `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}/audit`,
        ),
        fetchCaseSummary(),
      ]);
      setCaseDetail(caseOut.case);
      setMessages(messageOut.items);
      setAuditEvents(auditOut.items);
      setCaseSummary(summaryOut);
    } catch (err) {
      setError(friendlyConsoleError(err));
    } finally {
      setLoading(false);
    }
  }, [caseId, fetchCaseSummary, request, tenant_id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patchCase(
    action: 'ASSUME' | 'DONE' | 'CANCEL' | 'SPAM' | 'RETURN_TO_BOT',
    body: Record<string, unknown>,
    successMessage: string,
  ) {
    if (!tenant_id || !caseId) return;
    setError(null);
    setSuccess(null);
    setSavingAction(action);
    try {
      const out = await request<{ case: CaseDetail }>(
        `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ action, ...body }),
        },
      );
      setCaseDetail(out.case);
      setAuditEvents(
        (out.case.case_audit ?? []).map((audit) => ({
          audit_id: audit.audit_id,
          action: audit.event,
          actor_type: audit.actor_type,
          actor_id: audit.actor_id,
          previous_state: { status: audit.previous_status },
          new_state: { status: audit.new_status },
          reason: audit.reason,
          created_at: audit.created_at,
        })),
      );
      if (action === 'RETURN_TO_BOT') {
        setMessages((current) => prependReturnToBotHistoryEvent(out.case, current));
        setReturnReason('');
      }
      if (action === 'DONE' || action === 'CANCEL' || action === 'SPAM') {
        setCloseReason('');
      }
      setSuccess(successMessage);
    } catch (err) {
      setError(friendlyConsoleError(err));
    } finally {
      setSavingAction(null);
    }
  }

  async function submitReply(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !caseId) return;
    setError(null);
    setSuccess(null);
    setSavingAction('REPLY');
    const suggestionPayload = replySuggestion
      ? {
          suggestion_id: replySuggestion.suggestion_id,
          suggestion_text: replySuggestion.text,
          suggestion_template_key: replySuggestion.template_key,
          suggestion_template_id: replySuggestion.template_id,
          suggestion_template_version: replySuggestion.template_version,
          suggestion_edited: replyText.trim() !== replySuggestion.text.trim(),
        }
      : {};
    try {
      const out = await request<ReplyResult>(
        `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ text: replyText, ...suggestionPayload }),
        },
      );
      setCaseDetail(out.case);
      setAuditEvents(
        (out.case.case_audit ?? []).map((audit) => ({
          audit_id: audit.audit_id,
          action: audit.event,
          actor_type: audit.actor_type,
          actor_id: audit.actor_id,
          previous_state: { status: audit.previous_status },
          new_state: { status: audit.new_status },
          reason: audit.reason,
          created_at: audit.created_at,
        })),
      );
      setMessages((current) => [...current, out.message]);
      void refreshCaseSummary();
      setReplyText('');
      setReplySuggestion(null);
      if (out.delivery.outbox_status === 'SENT') {
        setSuccess('Resposta enviada pelo WhatsApp.');
      } else {
        setError(
          `Resposta registrada no outbox, mas nao enviada: ${out.delivery.error ?? out.delivery.outbox_status}`,
        );
      }
    } catch (err) {
      setError(friendlyConsoleError(err));
    } finally {
      setSavingAction(null);
    }
  }

  async function generateReplySuggestion() {
    if (!tenant_id || !caseId) return;
    setError(null);
    setSuccess(null);
    setSuggestionLoading(true);
    try {
      const out = await request<ReplySuggestionResult>(
        `/tenants/${encodeURIComponent(tenant_id)}/cases/${encodeURIComponent(caseId)}/reply-suggestion`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        },
      );
      setReplySuggestion(out.suggestion);
      setReplyText(out.suggestion.text);
    } catch (err) {
      setError(friendlyConsoleError(err));
    } finally {
      setSuggestionLoading(false);
    }
  }

  async function returnToBot(event: FormEvent) {
    event.preventDefault();
    await patchCase(
      'RETURN_TO_BOT',
      { close_reason: returnReason || null },
      'Atendimento humano encerrado. Bot ativo para a proxima mensagem do cliente.',
    );
  }

  async function closeCase(event: FormEvent) {
    event.preventDefault();
    await patchCase(
      'DONE',
      { outcome: closeOutcome, close_reason: closeReason },
      'Case encerrado com outcome registrado.',
    );
  }

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!caseId) return <main className="page">Case invalido.</main>;

  const busy = Boolean(savingAction);
  const mayAssume = hasScope('CASE_ASSUME');
  const mayReply = hasScope('CASE_REPLY');
  const mayClose = hasScope('CASE_CLOSE');
  const mayReturnToBot = hasScope('CASE_RETURN_TO_BOT');
  const mayMarkSpam = hasScope('CASE_MARK_SPAM');
  const canAssume = mayAssume && canAssumeCase(caseDetail);
  const canReply = mayReply && canSendHumanReply(caseDetail);
  const canClose = mayClose && canCloseCase(caseDetail);
  const canSpam = mayMarkSpam && canCloseCase(caseDetail);
  const canReturn = mayReturnToBot && canReturnToBot(caseDetail);

  return (
    <main className="page">
      <Link to="/">Voltar para inbox</Link>
      <header className="page-header">
        <div>
          <h1>Case {caseId}</h1>
          <p>Conversa {caseDetail?.conversation_id ?? '-'}</p>
        </div>
      </header>

      {loading ? <p>Carregando...</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {success ? <p className="success">{success}</p> : null}

      {caseDetail ? (
        <section className="case-layout">
          <article className="panel">
            <h2>Atendimento</h2>
            <dl>
              <dt>Cliente</dt>
              <dd>{caseDetail.customer_phone}</dd>
              <dt>Status</dt>
              <dd>{caseDetail.status}</dd>
              <dt>Bot</dt>
              <dd>{botStatusLabel(caseDetail)}</dd>
              <dt>Responsavel</dt>
              <dd>{ownerLabel(caseDetail)}</dd>
              <dt>Handoff ativo</dt>
              <dd>{caseDetail.active_handoff_id ?? 'Nenhum'}</dd>
              <dt>Inicio</dt>
              <dd>{formatDate(caseDetail.handoff_started_at)}</dd>
              <dt>Outcome</dt>
              <dd>{caseDetail.outcome ?? '-'}</dd>
              <dt>Ultimo motivo</dt>
              <dd>{caseDetail.handoff_close_reason ?? '-'}</dd>
            </dl>

            <section className="next-steps">
              <h3>Proximos passos</h3>
              <ul>
                {nextStepsForCase(caseDetail).map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </section>
            {summaryError ? <p className="hint">{summaryError}</p> : null}
            {caseSummary ? (
              <section className="case-summary">
                <div className="summary-heading">
                  <h3>Resumo</h3>
                  <small>
                    Atualizado{' '}
                    {formatDate(caseSummary.source_last_message_at ?? caseSummary.case_updated_at)}
                  </small>
                </div>
                <div className="summary-grid">
                  <div>
                    <span>Intencao</span>
                    <strong>{caseSummary.intent}</strong>
                  </div>
                  <div>
                    <span>Status</span>
                    <strong>{caseSummary.status}</strong>
                  </div>
                  <div>
                    <span>Prioridade</span>
                    <strong>{caseSummary.priority}</strong>
                  </div>
                </div>

                <h4>Dados coletados</h4>
                {caseSummary.collected_data.length ? (
                  <dl className="summary-data-list">
                    {caseSummary.collected_data.map((item) => (
                      <div key={`${item.label}-${item.value}`}>
                        <dt>{item.label}</dt>
                        <dd>
                          {item.value}
                          {item.caution ? <small>{item.caution}</small> : null}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="hint">Nenhum dado estruturado.</p>
                )}

                <h4>Pendencias</h4>
                {caseSummary.pending_items.length ? (
                  <ul className="summary-list">
                    {caseSummary.pending_items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="hint">Sem pendencias detectadas.</p>
                )}

                <h4>Ultimas interacoes</h4>
                {caseSummary.latest_interactions.length ? (
                  <ol className="summary-list">
                    {caseSummary.latest_interactions.map((item, index) => (
                      <li key={`${item.at ?? 'sem-data'}-${index}`}>
                        <small>
                          {item.direction} - {formatDate(item.at)}
                          {item.sensitive ? ' - sanitizada' : ''}
                        </small>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="hint">Sem interacoes recentes.</p>
                )}

                {caseSummary.privacy.cautions.length ? (
                  <p className="hint">{caseSummary.privacy.cautions.join('. ')}.</p>
                ) : null}
              </section>
            ) : null}

            {mayAssume ? (
              <div className="action-row">
                <button
                  type="button"
                  disabled={!canAssume || busy}
                  onClick={() => void patchCase('ASSUME', {}, 'Atendimento assumido.')}
                >
                  {savingAction === 'ASSUME' ? 'Assumindo...' : 'Assumir'}
                </button>
              </div>
            ) : null}

            {mayReply ? (
              <form className="handoff-form" onSubmit={submitReply}>
                <h3>Resposta humana</h3>
                <div className="reply-toolbar">
                  <button
                    type="button"
                    className="secondary"
                    disabled={!canReply || busy || suggestionLoading}
                    onClick={() => void generateReplySuggestion()}
                  >
                    {suggestionLoading ? 'Gerando...' : 'Gerar sugestao'}
                  </button>
                  {replySuggestion ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busy || suggestionLoading}
                      onClick={() => {
                        if (replyText === replySuggestion.text) setReplyText('');
                        setReplySuggestion(null);
                      }}
                    >
                      Limpar sugestao
                    </button>
                  ) : null}
                </div>
                {replySuggestion ? (
                  <div className="suggestion-meta">
                    <span>Sugestao</span>
                    <span>Template {replySuggestion.template_key}</span>
                    <span>
                      {replyText.trim() === replySuggestion.text.trim() ? 'Sem edicao' : 'Editada'}
                    </span>
                  </div>
                ) : null}
                <label>
                  Mensagem
                  <textarea
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    rows={5}
                  />
                </label>
                <button type="submit" disabled={!canReply || !replyText.trim() || busy}>
                  {savingAction === 'REPLY' ? 'Enviando...' : 'Enviar resposta'}
                </button>
                {!canReply ? <p className="hint">Resposta bloqueada para o status atual.</p> : null}
              </form>
            ) : null}

            {mayClose || mayMarkSpam ? (
              <form className="handoff-form" onSubmit={closeCase}>
                <h3>Encerramento</h3>
                <label>
                  Outcome
                  <select
                    value={closeOutcome}
                    onChange={(event) => setCloseOutcome(event.target.value)}
                  >
                    {OUTCOME_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Motivo
                  <textarea
                    value={closeReason}
                    onChange={(event) => setCloseReason(event.target.value)}
                    rows={3}
                  />
                </label>
                <div className="action-grid">
                  {mayClose ? (
                    <>
                      <button type="submit" disabled={!canClose || !closeReason.trim() || busy}>
                        {savingAction === 'DONE' ? 'Encerrando...' : 'Encerrar'}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={!canClose || !closeReason.trim() || busy}
                        onClick={() =>
                          void patchCase(
                            'CANCEL',
                            { outcome: 'CANCELLED', close_reason: closeReason },
                            'Case cancelado.',
                          )
                        }
                      >
                        Cancelar
                      </button>
                    </>
                  ) : null}
                  {mayMarkSpam ? (
                    <button
                      type="button"
                      className="danger"
                      disabled={!canSpam || !closeReason.trim() || busy}
                      onClick={() =>
                        void patchCase(
                          'SPAM',
                          { outcome: 'SPAM_OR_TEST', close_reason: closeReason },
                          'Case marcado como spam ou teste.',
                        )
                      }
                    >
                      Spam/teste
                    </button>
                  ) : null}
                </div>
              </form>
            ) : null}

            {mayReturnToBot ? (
              <form className="handoff-form" onSubmit={returnToBot}>
                <h3>Devolver ao bot</h3>
                <label>
                  Motivo
                  <textarea
                    value={returnReason}
                    onChange={(event) => setReturnReason(event.target.value)}
                    rows={3}
                  />
                </label>
                <button type="submit" disabled={!canReturn || busy}>
                  {savingAction === 'RETURN_TO_BOT' ? 'Devolvendo...' : 'Devolver ao bot'}
                </button>
                {!canReturn ? (
                  <p className="hint">Devolucao indisponivel para o status atual.</p>
                ) : null}
              </form>
            ) : null}

            {auditEvents.length ? (
              <section className="audit-list">
                <h3>Auditoria</h3>
                <ul>
                  {auditEvents.slice(-10).map((audit) => (
                    <li key={audit.audit_id}>
                      {audit.action}: {String(audit.previous_state?.status ?? '-')} para{' '}
                      {String(audit.new_state?.status ?? '-')} por{' '}
                      {audit.actor_id ?? audit.actor_type} em {formatDate(audit.created_at)}
                      {audit.reason ? ` - ${audit.reason}` : ''}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </article>

          <article className="panel">
            <h2>Historico</h2>
            {messages.length ? (
              <ol className="message-list">
                {messages.map((message) => (
                  <li key={message.log_id}>
                    <small>
                      {message.direction ?? 'LOG'} -{' '}
                      {formatDate(message.created_at ?? message.timestamp)}
                    </small>
                    <p>{messageText(message)}</p>
                  </li>
                ))}
              </ol>
            ) : (
              <p>Nenhuma mensagem encontrada.</p>
            )}
          </article>
        </section>
      ) : null}
    </main>
  );
}
